import {
  MutationCache,
  QueryClient,
  type Register,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { getErrorCode, getErrorMessage } from '@/lib/api-error'
import { usePaywallStore } from '@/stores/paywall-store'

/**
 * Mutation `meta` opt-outs. Forms that surface their own error message
 * inline (login, signup, password reset, etc.) should set
 * `meta: { skipGlobalErrorToast: true }` to avoid the double-noise of an
 * inline error AND a top-right toast.
 */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      skipGlobalErrorToast?: boolean
    }
  }
}
// Re-exported so the augmentation is preserved through bundler tree-shaking.
export type { Register }

/**
 * Centralized mutation error handler. Translates backend `error.code` into
 * a toast (and a paywall modal where appropriate). Per-mutation `onError`
 * callbacks still run — TanStack Query fires both — and they can take over
 * the UI if they want different behavior.
 */
function handleMutationError(
  error: unknown,
  _vars: unknown,
  _ctx: unknown,
  mutation: { meta?: Record<string, unknown> | undefined },
) {
  if (mutation.meta?.skipGlobalErrorToast === true) return

  const code = getErrorCode(error)
  const message = getErrorMessage(error)

  // Auth errors don't need a toast — the api-client interceptor already
  // redirects to /login. Showing a toast on top would be noise.
  if (code === 'AUTHENTICATION_REQUIRED' || code === 'TOKEN_EXPIRED') {
    return
  }

  if (code === 'INSUFFICIENT_CREDITS') {
    usePaywallStore.getState().open({
      reason: 'insufficient-credits',
      message,
    })
    return
  }

  // 403 with a tier-related message → paywall, otherwise a plain toast.
  if (code === 'FORBIDDEN' && /tier|plan|upgrade|subscription/i.test(message)) {
    usePaywallStore.getState().open({
      reason: 'tier-required',
      message,
    })
    return
  }

  toast.error(message)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
})
